/* eslint-disable @typescript-eslint/camelcase */
import {
  AssetData,
  ColorData,
  ExternalMediumData,
  FontData,
  GenericFileData,
  PersonData,
  PressData,
  Asset,
  LinkObject
} from '@api/v4/assets/assetTypes';
import {
  ReducerState
} from '@components/asset/modal/tabs/edit/EditTabTypes';
import { isDirty, transformColorData } from '@components/asset/modal/tabs/edit/helpers';

interface CreateAttachmentProperties {
  filename: string;
  key: string;
  position: number;
  url: string;
}

// these get placed in the API call directly
// try and keep field names the same as ones returned
// from api
export interface AssetChanges {
  attachments: CreateAttachmentProperties[];
  data: AssetData;
  description: string;
  name: string;
  thumbnail_override: LinkObject;
  watermark: LinkObject;
}

enum AssetValues {
  Attachments,
  Data,
  Description,
  Name,
  ThumbnailOverrideChangeKey,
  WatermarkChangeKey
}

type getDataChangesFn = (changes: Partial<AssetChanges>, state: ReducerState) => Partial<AssetChanges>;

const getAssetData = <T extends AssetData>(asset: Asset | null): T | null => {
  if (asset) {
    return asset.data as T;
  }

  return null;
};

const getColorChanges: getDataChangesFn = (changes, state) => {
  const initialColorData = getAssetData<ColorData>(state.initialData.asset);
  if (isDirty<ColorData>(initialColorData, state.editState.colorData)) {
    return {
      ...changes,
      data: transformColorData(state.editState.colorData)
    };
  }

  return changes;
};

const getExternalMediumChanges: getDataChangesFn = (changes, state) => {
  const initialExternalMediumData = getAssetData<ExternalMediumData>(state.initialData.asset);
  if (isDirty<ExternalMediumData>(initialExternalMediumData, state.editState.externalMediumData)) {
    return {
      ...changes,
      data: state.editState.externalMediumData
    };
  }

  return changes;
};

const getFontChanges: getDataChangesFn = (changes, state) => {
  const initialFontData = getAssetData<FontData>(state.initialData.asset);
  if (isDirty<FontData>(initialFontData, state.editState.fontData)) {
    return {
      ...changes,
      data: state.editState.fontData
    };
  }

  return changes;
};

const getPersonChanges: getDataChangesFn = (changes, state) => {
  const initialPersonData = getAssetData<PersonData>(state.initialData.asset);
  if (isDirty<PersonData>(initialPersonData, state.editState.personData)) {
    return {
      ...changes,
      data: state.editState.personData
    };
  }

  return changes;
};

const getPressChanges: getDataChangesFn = (changes, state) => {
  const initialPressData = getAssetData<PressData>(state.initialData.asset);
  if (isDirty<PressData>(initialPressData, state.editState.pressData)) {
    return {
      ...changes,
      data: state.editState.pressData
    };
  }

  return changes;
};

const getGenericFileDataChanges: getDataChangesFn = (changes, state) => {
  const initialGenericFileData = getAssetData<GenericFileData>(state.initialData.asset);
  if (isDirty<GenericFileData>(initialGenericFileData, state.editState.genericFileData)) {
    return {
      ...changes,
      data: state.editState.genericFileData
    };
  }
  return changes;
};

const getNewAttachments = (changes: Partial<AssetChanges>, state: ReducerState): Partial<AssetChanges> => {
  const { editState, initialData } = state;
  const newAttachments: CreateAttachmentProperties[] = [];
  editState.attachments.forEach((editAttachment) => {
    const matchingAttachment = initialData.attachments.find((initialAttachment) => initialAttachment.key === editAttachment.key);
    if (!matchingAttachment) {
      const { key, filename, position, url } = editAttachment;
      newAttachments.push({ key, filename, position, url });
    }
  });

  return newAttachments.length > 0
    ? {
      ...changes,
      attachments: newAttachments
    }
    : changes;
};

const assetChangeMap = {
  [AssetValues.Attachments]: getNewAttachments,
  [AssetValues.Data]: (changes: Partial<AssetChanges>, state: ReducerState): Partial<AssetChanges> => {
    if (state.editState.colorData) {
      return getColorChanges(changes, state);
    }

    if (state.editState.externalMediumData) {
      return getExternalMediumChanges(changes, state);
    }

    if (state.editState.fontData) {
      return getFontChanges(changes, state);
    }

    if (state.editState.personData) {
      return getPersonChanges(changes, state);
    }

    if (state.editState.pressData) {
      return getPressChanges(changes, state);
    }

    if (state.editState.genericFileData) {
      return getGenericFileDataChanges(changes, state);
    }

    return changes;
  },
  [AssetValues.Description]: (changes: Partial<AssetChanges>, state: ReducerState): Partial<AssetChanges> => {
    const initialDescription = (state.initialData.asset && state.initialData.asset.description) || '';
    if (initialDescription !== state.editState.assetDescription) {
      return {
        ...changes,
        description: state.editState.assetDescription
      };
    }
    return changes;
  },

  [AssetValues.Name]: (changes: Partial<AssetChanges>, state: ReducerState): Partial<AssetChanges> => {
    const initialAssetName = (state.initialData.asset && state.initialData.asset.name) || '';
    return initialAssetName !== state.editState.assetName
      ? { ...changes, name: state.editState.assetName }
      : changes;
  },

  [AssetValues.ThumbnailOverrideChangeKey]: (changes: Partial<AssetChanges>, state: ReducerState): Partial<AssetChanges> => {
    const urlChanges = state.editState.thumbnailOverride.url !== state.initialData.asset?.thumbnail_override.url;
    return (urlChanges)
      ? { ...changes, thumbnail_override: { ...state.editState.thumbnailOverride } }
      : changes;
  },

  [AssetValues.WatermarkChangeKey]: (changes: Partial<AssetChanges>, state: ReducerState): Partial<AssetChanges> => {
    // need to null check initialData.asset?.watermark? because change engine fires before
    // we transform the asset
    const urlChanges = state.editState.watermark.url !== state.initialData.asset?.watermark?.url;
    return (urlChanges)
      ? { ...changes, watermark: { ...state.editState.watermark } }
      : changes;
  }

};

const getAssetChanges = (state): Partial<AssetChanges> | false => {
  let changes: Partial<AssetChanges> = {};
  changes = Object.keys(assetChangeMap).reduce((prevChanges, key) => assetChangeMap[key](prevChanges, state), changes);
  return Object.keys(changes).length > 0 && changes;
};

export default getAssetChanges;
